import type { App } from 'vue'
import { createRouter, createWebHistory } from 'vue-router'
import { setPageSeo, setPageTitle } from '@/utils/pageSeo'
import { hideLoading, showLoading } from '@/components/baseFullScreenLoading'
import useUserStoreUserInfo from '@/store/modules/userInfo'
import baseRoute from './route'

const routerHistory = createWebHistory()
const router = createRouter({
  history: routerHistory,
  routes: baseRoute
})

router.onError((error, to) => {
  const message = String((error as { message?: string })?.message || error)
  if (
    !message.includes('Failed to fetch dynamically imported module') &&
    !message.includes('Importing a module script failed')
  ) {
    return
  }
  const key = 'router-dynamic-import-reload'
  if (sessionStorage.getItem(key)) return
  sessionStorage.setItem(key, '1')
  window.location.assign(to.fullPath || window.location.href)
})

router.afterEach(to => {
  sessionStorage.removeItem('router-dynamic-import-reload')
  const meta = to.meta
  if (meta.seoTitle) {
    setPageSeo({
      title: meta.seoTitle as string,
      description: meta.seoDescription as string | undefined,
      keywords: meta.seoKeywords as string | undefined
    })
    return
  }
  if (meta.title) {
    setPageTitle(meta.title as string)
  }
})

const hasLoginUser = (userInfo: { userId?: unknown } | null | undefined) =>
  Boolean(userInfo?.userId)

router.beforeEach(async (to, _from, next) => {
  if (to.meta.notAuth) return next()
  const userInfoStore = useUserStoreUserInfo()
  // persist 已关：内存 userId + 本页签 verified 才跳过探测。只信其一会被注入绕过。
  const needProbe =
    !hasLoginUser(userInfoStore.userInfo) || !userInfoStore.isTabVerified()
  if (needProbe) {
    showLoading()
    try {
      await userInfoStore.getUserInfo({
        noAuthRedirect: true,
        noLoading: true
      })
    } catch {
      userInfoStore.clearInfo()
    } finally {
      hideLoading()
    }
  }
  if (!hasLoginUser(userInfoStore.userInfo)) {
    return next({ name: 'Login' })
  }
  next()
})

export function setupRouter(app: App<Element>): void {
  app.use(router)
}
export default router
